import { useState } from 'react';

import { Check, ChevronDown } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LaneReasoning, RuleEvaluation } from 'types/agents/QuickQuoteAgent';
import { cn } from 'utils/shadcn';

import {
  JudgeSummaryKind,
  formatDollars,
  groupRuleEvaluations,
  summarizeJudge,
} from './reasoningHelpers';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant='body-xxs'
      weight='semibold'
      textColor='muted'
      className='uppercase leading-normal tracking-wide'
    >
      {children}
    </Typography>
  );
}

function RuleId({ id }: { id: string }) {
  return (
    <Typography
      as='span'
      variant='body-xxs'
      weight='semibold'
      className='mr-1 inline-block rounded-[3px] bg-neutral-200 px-1 leading-4 text-neutral-700 tabular-nums'
    >
      {id}
    </Typography>
  );
}

function RuleList({
  evaluations,
  evidenceClassName,
}: {
  evaluations: RuleEvaluation[];
  evidenceClassName?: string;
}) {
  return (
    <ul className='flex flex-col gap-1.5'>
      {evaluations.map((evaluation) => (
        <li key={evaluation.ruleId} className='text-[11.5px] leading-snug'>
          <Typography
            variant='body-xs'
            className='text-[11.5px] leading-snug text-neutral-800'
          >
            <RuleId id={evaluation.ruleId} />
            {evaluation.ruleText}
          </Typography>
          {evaluation.evidence && (
            <Typography
              variant='body-xs'
              className={cn(
                'mt-0.5 pl-0.5 text-[11px] leading-snug text-neutral-600',
                evidenceClassName
              )}
            >
              {evaluation.evidence}
            </Typography>
          )}
        </li>
      ))}
    </ul>
  );
}

function Ledger({ lane }: { lane: LaneReasoning }) {
  const steps = lane.steps ?? [];
  if (steps.length === 0) {
    return null;
  }

  return (
    <Flex direction='col' gap='xs' className='w-full'>
      <SectionLabel>How the price adds up</SectionLabel>
      <ol className='w-full text-[11.5px] tabular-nums'>
        {steps.map((step, index) => (
          <li
            key={`${step.op}-${index}`}
            className='flex items-baseline justify-between gap-3 border-b border-neutral-200 py-1 last:border-b-0'
          >
            <Typography
              as='span'
              variant='body-xs'
              className='text-[11.5px] leading-snug text-neutral-700'
            >
              {step.label}
            </Typography>
            <Typography
              as='span'
              variant='body-xs'
              className='shrink-0 text-[11.5px] leading-snug text-neutral-800'
            >
              {formatDollars(step.after)}
            </Typography>
          </li>
        ))}
        {lane.sellRate !== undefined && (
          <li className='flex items-baseline justify-between gap-3 border-t border-neutral-400 pt-1'>
            <Typography
              as='span'
              variant='body-xs'
              weight='semibold'
              className='text-[11.5px] leading-snug'
            >
              Sell rate
            </Typography>
            <Typography
              as='span'
              variant='body-xs'
              weight='semibold'
              className='shrink-0 text-[11.5px] leading-snug'
            >
              {formatDollars(lane.sellRate)}
            </Typography>
          </li>
        )}
      </ol>
    </Flex>
  );
}

// A quiet confirmation, or the open questions in plain words. No pass counts.
function DoubleCheck({ lane }: { lane: LaneReasoning }) {
  const summary = summarizeJudge(lane);

  if (summary.kind === JudgeSummaryKind.None) {
    return null;
  }

  if (summary.kind === JudgeSummaryKind.Passed) {
    return (
      <Flex align='center' gap='xs' data-testid='agent-double-check'>
        <Check className='h-3 w-3 shrink-0 text-neutral-500' aria-hidden />
        <Typography
          as='span'
          variant='body-xs'
          textColor='muted'
          className='text-[11px] leading-normal'
        >
          Double-checked against your rules
        </Typography>
      </Flex>
    );
  }

  const issues = summary.verdict.issues ?? [];
  if (issues.length === 0) {
    return null;
  }

  return (
    <Flex
      direction='col'
      gap='xs'
      className='w-full'
      data-testid='agent-double-check'
    >
      <SectionLabel>Please check</SectionLabel>
      <ul className='flex flex-col gap-1'>
        {issues.map((issue, index) => (
          <Typography
            key={`${issue.ruleId}-${index}`}
            as='li'
            variant='body-xs'
            className='text-[11.5px] leading-snug text-neutral-700'
          >
            {issue.ruleId && <RuleId id={issue.ruleId} />}
            {issue.problem}
          </Typography>
        ))}
      </ul>
    </Flex>
  );
}

type FullReasoningProps = {
  lane: LaneReasoning;
};

// Everything behind "Show full reasoning": price steps, rules, overlaps, double-check.
export function FullReasoning({ lane }: FullReasoningProps) {
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const { applied, couldNotEvaluate, notApplicable } =
    groupRuleEvaluations(lane);
  const conflicts = lane.conflicts ?? [];

  return (
    <Flex
      direction='col'
      gap='md'
      className='w-full pt-2'
      data-testid='agent-full-reasoning'
    >
      <Ledger lane={lane} />

      {applied.length > 0 && (
        <Flex direction='col' gap='xs' className='w-full'>
          <SectionLabel>Rules Kit used</SectionLabel>
          <RuleList evaluations={applied} />
        </Flex>
      )}

      {couldNotEvaluate.length > 0 && (
        <Flex direction='col' gap='xs' className='w-full'>
          <SectionLabel>Rules Kit couldn&apos;t confirm</SectionLabel>
          <RuleList
            evaluations={couldNotEvaluate}
            evidenceClassName='text-neutral-600 italic'
          />
        </Flex>
      )}

      {conflicts.length > 0 && (
        <Flex direction='col' gap='xs' className='w-full'>
          <SectionLabel>Where rules overlapped</SectionLabel>
          <ul className='flex flex-col gap-1.5'>
            {conflicts.map((conflict) => (
              <li
                key={conflict.ruleIds.join('-')}
                className='text-[11.5px] leading-snug text-neutral-700'
              >
                {conflict.ruleIds.map((id) => (
                  <RuleId key={id} id={id} />
                ))}
                {conflict.resolution}
              </li>
            ))}
          </ul>
        </Flex>
      )}

      {notApplicable.length > 0 && (
        <Flex direction='col' gap='xs' className='w-full'>
          <button
            type='button'
            aria-expanded={showNotApplicable}
            onClick={() => setShowNotApplicable((open) => !open)}
            className={cn(
              'text-[10px] font-semibold uppercase leading-normal tracking-wide text-neutral-500',
              'flex cursor-pointer items-center gap-1 hover:text-neutral-700'
            )}
          >
            Didn&apos;t fit this load ({notApplicable.length})
            <ChevronDown
              className={cn(
                'h-3 w-3 transition-transform',
                showNotApplicable && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
          {showNotApplicable && (
            <RuleList
              evaluations={notApplicable}
              evidenceClassName='text-neutral-500'
            />
          )}
        </Flex>
      )}

      <DoubleCheck lane={lane} />
    </Flex>
  );
}
